import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authAPI } from '../lib/auth-api';
import { useAuthStore } from '../stores/auth-store';
import { handleSessionExpired, isSessionExpiredError } from '../lib/session';
import type { User, Customer } from '../types';

// Development-only bypass authentication
const DEV_MODE = import.meta.env.MODE === 'development';
const BYPASS_AUTH = DEV_MODE && new URLSearchParams(window.location.search).has('bypass_auth');

const MOCK_USER: User = {
  sub: 'dev-user-123',
  email: 'dev@example.com',
  role: 'admin',
  customer_id: 'dev-customer-123',
  company_name: 'Development Company',
};

const MOCK_CUSTOMER: Customer = {
  id: 'dev-customer-123',
  name: 'Development Company',
  cognito_user_id: 'dev-cognito-123',
};

export function useAuth() {
  const { user, customer, setUser, setCustomer } = useAuthStore();

  // Bypass authentication in development mode with ?bypass_auth parameter
  useEffect(() => {
    if (BYPASS_AUTH && !user) {
      console.warn('[DEV MODE] Bypassing authentication with mock user');
      setUser(MOCK_USER);
      setCustomer(MOCK_CUSTOMER);
    }
  }, [user, setUser, setCustomer]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authAPI.getMe(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !BYPASS_AUTH, // Skip API call if bypassing auth
    refetchOnMount: false, // Don't refetch on mount if we have data
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Handle 401 errors by redirecting to login
  useEffect(() => {
    if (isSessionExpiredError(error)) {
      console.log('[useAuth] Session expired, clearing state and redirecting to login');
      handleSessionExpired();
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      console.log('[useAuth] Processing auth data:', data);
      setUser(data);

      // Create synthetic customer object from user data
      const syntheticCustomer: Customer = {
        id: data.customer_id,
        name: data.company_name || 'Unknown Company',
        cognito_user_id: data.sub,
      };
      console.log('[useAuth] Created synthetic customer:', syntheticCustomer);
      setCustomer(syntheticCustomer);
    }
  }, [data, setUser, setCustomer]);

  return {
    user,
    customer,
    isAuthenticated: !!user,
    isLoading,
    error,
  };
}
